import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { StorageService } from './shared/db/StorageService';
import {
  NetworkService,
  ConnectionStatus,
} from './shared/services/network.service';
import { OfflineManagerService } from './shared/services/offline-manager.service';
import { IonicModule, Platform, MenuController } from '@ionic/angular';
import { SplashScreen } from '@capacitor/splash-screen';
import { AlertController } from '@ionic/angular';
import { App } from '@capacitor/app';
import { ThemeService } from './shared/services/theme.service';
import { LanguageService } from './shared/services/language.service';
import { UpdateService } from './shared/services/update.service';
import { TranslateService } from '@ngx-translate/core';
import { GeneralStateContext } from './general-state/app.general.state';
import { MenuComponent } from './shared/components/menu/menu.component';
import { PermissionService } from './shared/services/permission.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, MenuComponent],
})
export class AppComponent {
  private storageService = inject(StorageService);
  private platform = inject(Platform);
  private offlineManager = inject(OfflineManagerService);
  private networkService = inject(NetworkService);
  private alertController = inject(AlertController);
  private themeService = inject(ThemeService);
  private languageService = inject(LanguageService);
  private translateService = inject(TranslateService);
  private updateService = inject(UpdateService);
  permissionService = inject(PermissionService);
  generalState = inject(GeneralStateContext);
  menuCtrl = inject(MenuController);

  isMenuEnabled = computed(() => this.generalState.isMenuState());

  constructor() {
    this.initializeApp();
    this.menuCtrl.enable(false, 'main');
  }

  initializeApp() {
    this.platform.ready().then(() => {
      // Warm-up de la BBDD local (lazy-ready: cualquier operación la crea).
      // En nativo debe ocurrir tras platform.ready() para que el driver
      // SQLite esté disponible.
      this.storageService
        .initDB()
        .catch((error) =>
          console.error('Error inicializando la BBDD local', error),
        );

      // Aplicar el tema persistido — independiente del resto del arranque,
      // y sin tragar errores en silencio.
      this.themeService
        .applyThemeMode()
        .catch((error) =>
          console.error('No se pudo aplicar el tema persistido', error),
        );

      // Aplicar el idioma persistido (default: en-US) — mismo patrón que el tema.
      this.languageService
        .applyLanguage()
        .catch((error) =>
          console.error('No se pudo aplicar el idioma persistido', error),
        );

      // PWA: avisar con un toast cuando el SW tenga una versión nueva lista
      // (no hace nada en dev ni en nativo — SwUpdate.isEnabled es false).
      this.updateService.init();

      // PWA: pedir almacenamiento persistente — sin esto, iOS/Android puede
      // evictar IndexedDB bajo presión y se perdería la cola offline y los
      // datos espejo. No-op en nativo y en browsers que no lo soportan.
      this.permissionService.request('storage-persistence').catch(() => {});

      // Listening Network Status + auto-sync al recuperar conexión
      this.networkService.status$.subscribe((status: ConnectionStatus) => {
        if (status === ConnectionStatus.Online) {
          this.offlineManager.checkForEvents().subscribe();
        }
      });

      // StatusBar.setOverlaysWebView({ overlay: true });
      SplashScreen.hide();

      // Handle hardware back button
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.presentAlert();
      });
    });
  }

  async presentAlert() {
    const alert = await this.alertController.create({
      header: this.translateService.instant('APP.EXIT_CONFIRM') as string,
      buttons: [
        {
          text: this.translateService.instant('COMMON.CANCEL') as string,
          role: 'cancel',
          handler: () => {},
        },
        {
          text: this.translateService.instant('COMMON.OK') as string,
          role: 'confirm',
          handler: () => {
            App.exitApp();
          },
        },
      ],
    });
    await alert.present();
  }
}
